import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { AdminTabs } from "../components/admin/AdminTabs";
import { BookingDetailDialog } from "../components/admin/BookingDetailDialog";
import { BOOKABLE_CONSULTANTS } from "../data/consultants";

const TZ = "Europe/London";
const ROWS = [
  ...BOOKABLE_CONSULTANTS.map((c, i) => ({ slug: c.slug, name: c.name, cls: ["bg-sky-50 border-sky-300", "bg-emerald-50 border-emerald-300", "bg-amber-50 border-amber-300"][i % 3] })),
  { slug: "", name: "First available", cls: "bg-stone-100 border-stone-300" },
];
const ymd = (d) => d.toLocaleDateString("en-CA", { timeZone: TZ });
const mondayOf = (d) => { const x = new Date(`${ymd(d)}T12:00:00Z`); x.setUTCDate(x.getUTCDate() - ((x.getUTCDay() + 6) % 7)); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setUTCDate(x.getUTCDate() + n); return x; };
const time = (iso) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: TZ });

const Chip = ({ b, cls, onOpen }) => (
  <button type="button" onClick={() => onOpen(b)} data-testid={`cal-booking-${b.id}`}
    className={`w-full text-left px-2 py-1 rounded-sm border text-xs ${cls} ${b.state === "pending" ? "border-dashed opacity-70" : ""} hover:shadow-sm transition-shadow`}>
    <span className="font-medium">{time(b.start_time)}</span> {b.patient_name || "—"}
    {b.visit_type === "Follow-up consultation" && <span className="ml-1 text-[10px] uppercase tracking-wide text-ink-muted">F/U</span>}
    {b.state === "pending" && <span className="block text-[10px] text-ink-muted">awaiting payment</span>}
  </button>
);

export default function AdminCalendar() {
  const { user } = useAuth();
  const [monday, setMonday] = useState(() => mondayOf(new Date()));
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(monday, i)), [monday]);

  const load = useCallback(() => {
    setRows(null); setError("");
    api.get("/admin/calendar", { params: { start: ymd(monday) } }).then(({ data }) => setRows(data.bookings))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [monday]);
  useEffect(() => { load(); }, [load]);

  const cell = (slug, d) => (rows || []).filter((b) => (b.consultant_slug || "") === slug && ymd(new Date(b.start_time)) === ymd(d));

  return (
    <div data-testid="admin-calendar-page" className="container-ccc py-12">
      <div className="eyebrow">Admin · {user?.email}</div>
      <h1 className="font-serif text-4xl mt-2">Calendar</h1>
      <AdminTabs active="/admin/calendar" />
      <div className="mt-6 flex items-center gap-3">
        <button type="button" onClick={() => setMonday(addDays(monday, -7))} data-testid="cal-prev-week" className="p-2 rounded-full border border-stone-300 hover:bg-cream-100" aria-label="Previous week"><CaretLeft size={16} /></button>
        <div data-testid="cal-week-label" className="font-medium min-w-[220px] text-center">
          Week of {monday.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })}
        </div>
        <button type="button" onClick={() => setMonday(addDays(monday, 7))} data-testid="cal-next-week" className="p-2 rounded-full border border-stone-300 hover:bg-cream-100" aria-label="Next week"><CaretRight size={16} /></button>
        <button type="button" onClick={() => setMonday(mondayOf(new Date()))} data-testid="cal-this-week" className="text-sm text-bronze-dark underline underline-offset-4 ml-2">This week</button>
        <span className="text-sm text-ink-muted ml-auto" data-testid="cal-count">{rows ? `${rows.length} booking${rows.length === 1 ? "" : "s"}` : "Loading…"}</span>
      </div>
      {error && <div className="mt-4 text-sm text-destructive">{error}</div>}
      <div className="mt-6 overflow-x-auto bg-white border border-stone-200/70 rounded-md">
        <table data-testid="cal-grid" className="w-full text-sm table-fixed min-w-[1000px]">
          <thead>
            <tr>
              <th className="w-44 p-3 text-left text-[11px] uppercase tracking-[0.14em] text-ink-muted font-medium">Consultant</th>
              {days.map((d) => (
                <th key={ymd(d)} className={`p-3 text-left font-medium ${ymd(d) === ymd(new Date()) ? "text-bronze-dark" : "text-ink"}`}>
                  {d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" })}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r) => (
              <tr key={r.slug || "any"} data-testid={`cal-row-${r.slug || "any"}`} className="border-t border-stone-200/60 align-top">
                <td className="p-3 font-medium">{r.name}</td>
                {days.map((d) => (
                  <td key={ymd(d)} className="p-2 space-y-1 border-l border-stone-100">
                    {cell(r.slug, d).map((b) => <Chip key={b.id} b={b} cls={r.cls} onOpen={setOpen} />)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <BookingDetailDialog booking={open} onClose={() => setOpen(null)} />
    </div>
  );
}
